import Cookies from "js-cookie"
import { type StateCreator, create } from "zustand"

import { COOKIE_CONFIG, TOKEN_MAP } from "../constants/auth"
import type { TokenUser, Tokens, UserAuthStatus } from "../types"
import { getTokenExpiry, parseJwtToken } from "../utils/auth"

import { redirectToSignIn, redirectToSignOut } from "@/utils/auth"
import { withDevtools } from "@/utils/auth/withDevtools"

interface AuthState {
  tokens: {
    idToken: string | null
    accessToken: string | null
    refreshToken: string | null
  }
  user: TokenUser | null
  status: UserAuthStatus
  initStore: () => void
  setTokens: (tokens: Tokens) => void
  setStatus: (status: AuthState["status"]) => void
  clearAuth: () => Promise<void>
  signIn: () => void
  signOut: () => Promise<void>
}

const storeInitialiser: StateCreator<AuthState> = (set, get) => ({
  tokens: {
    idToken: null,
    accessToken: null,
    refreshToken: null,
  },
  user: null,
  status: "idle",

  setStatus: (status) => {
    set({ status })
  },

  setTokens: (tokens) => {
    const newTokens = { ...get().tokens }
    let user: TokenUser | null = null
    Object.entries(TOKEN_MAP).forEach(([key, cookieKey]) => {
      const tokenKey = key as keyof Tokens
      const tokenValue = tokens[tokenKey]

      if (tokenValue) {
        const expires = getTokenExpiry(tokenKey, tokenValue)
        Cookies.set(cookieKey, tokenValue, { ...COOKIE_CONFIG, expires })
        newTokens[tokenKey] = tokenValue
      }
    })

    // update user sate if idToken and accessToken is present
    if (newTokens["idToken"] && newTokens["accessToken"]) {
      user = parseJwtToken(newTokens["idToken"], newTokens["accessToken"])
    } else {
      console.warn("Store: setTokens: idToken or accessToken is not present")
    }

    set({
      tokens: newTokens,
      user,
      status: user ? "authenticated" : "unauthenticated",
    })
  },

  clearAuth: async () => {
    Object.values(TOKEN_MAP).forEach((cookieKey) => {
      Cookies.remove(cookieKey, { path: "/" })
    })

    set({
      tokens: {
        idToken: null,
        accessToken: null,
        refreshToken: null,
      },
      user: null,
      status: "unauthenticated",
    })
  },

  initStore: () => {
    const idToken = Cookies.get("id_token") ?? null
    const accessToken = Cookies.get("access_token") ?? null
    const refreshToken = Cookies.get("refresh_token") ?? null
    const user = parseJwtToken(idToken, accessToken)
    const status = idToken && accessToken && user ? "authenticated" : "unauthenticated"
    set({
      tokens: { idToken, accessToken, refreshToken },
      user,
      status,
    })
  },

  signIn: () => {
    redirectToSignIn()
  },

  signOut: async () => {
    await get().clearAuth()
    redirectToSignOut()
  },
})

export const useAuthStore = create<AuthState>()(withDevtools(storeInitialiser, "AuthStore"))
